import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { PropertyRepository } from '../repositories/PropertyRepository'
import {
  createPropertySchema,
  type PropertyFormInput,
  type PropertyFormOutput,
} from '../schemas/propertySchema'
import type {
  Property,
  PropertyInsert,
  PropertyUpdate,
  StProperty,
} from '../types'
import { getDefaultValues } from '../utils/propertyFormDefaults'
import { useCRUD } from './useCRUD'
import { usePropertyFormSteps } from './usePropertyFormSteps'

interface UsePropertyFormRHFOptions {
  st_property: StProperty | null
  property?: Property | null
  onSuccess?: () => void
}

/**
 * Main hook for property form with React Hook Form
 * Orchestrates form state, validation, step navigation, and submission
 * Optimized for performance and minimal re-renders
 */
export function usePropertyFormRHF({
  st_property,
  property,
  onSuccess,
}: UsePropertyFormRHFOptions) {
  const { propertySchema, stepSchemas } = useMemo(
    () => createPropertySchema(st_property !== null),
    [st_property] // Empty dependency array since there are no parameters yet
  )

  // Memoized default values based on property
  const defaultValues = useMemo<PropertyFormInput<boolean>>(
    () => getDefaultValues(property, st_property),
    [property, st_property]
  )

  // Form setup with memoized default values and explicit type handling
  const form = useForm<
    PropertyFormInput<boolean>,
    any,
    PropertyFormOutput<boolean>
  >({
    resolver: zodResolver(propertySchema),
    mode: 'onBlur', // Changed from "onChange" to prevent aggressive validation triggering submission
    defaultValues,
  })

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    watch,
    formState: { errors },
  } = form

  const formValues = watch()

  // Memoized step manager
  const stepManager = usePropertyFormSteps({ trigger, stepSchemas })

  // Repository and CRUD setup
  const propertyRepository = useMemo(() => new PropertyRepository(), [])
  const { createAsync, updateAsync, isCreating, isUpdating } = useCRUD<
    Property,
    PropertyInsert,
    PropertyUpdate
  >({
    repository: propertyRepository,
    queryKey: ['property', 'properties'],
  })

  // Memoized navigation functions
  const goToStep = useCallback(
    (stepIndex: number) => {
      stepManager.goToStep(stepIndex, formValues)
    },
    [stepManager, formValues]
  )

  const nextStep = useCallback(() => {
    stepManager.nextStep(formValues)
  }, [stepManager, formValues])

  const prevStep = useCallback(() => {
    stepManager.prevStep(formValues)
  }, [stepManager, formValues])

  // Memoized validation functions
  const validateStep = useCallback(
    async (stepIndex: number): Promise<boolean> => {
      return stepManager.validateStep(stepIndex, formValues)
    },
    [stepManager, formValues]
  )

  const areRequiredStepsValid = useCallback((): boolean => {
    // For final submission validation, use the complete propertySchema
    // instead of relying on individual step validation
    try {
      propertySchema.parse(formValues)
      return true
    } catch {
      // console.error('Validation failed:', error)
      return false
    }
  }, [formValues, propertySchema])

  // Form submission handler
  const onSubmit = useCallback(
    async (data: PropertyFormOutput<boolean>) => {
      try {
        if (property?.id) {
          const { id, ...propertyData } = property
          const mergedData = { ...propertyData, ...data }
          console.log({ mergedData })
          await updateAsync({
            id,
            data: mergedData,
          })
        } else {
          await createAsync(data)
        }
        // Call the onSuccess callback after all operations are successful
        onSuccess?.()
      } catch (error) {
        console.error('Submission failed:', error)
        // Errors are handled by useCRUD's toast notifications
      }
    },
    [property, createAsync, updateAsync, onSuccess]
  )

  return {
    // Form
    form,
    control,
    errors,
    handleSubmit,
    onSubmit,

    // Steps
    steps: stepManager.steps,
    currentStep: stepManager.currentStep,
    goToStep,
    nextStep,
    prevStep,
    validateStep,
    areRequiredStepsValid,

    // Step utilities
    canGoNext: useCallback(
      () => stepManager.canGoNext(getValues()),
      [stepManager, getValues]
    ),
    canGoPrev: stepManager.canGoPrev,
    getCurrentStep: stepManager.getCurrentStep,
    isLastStep: stepManager.isLastStep,
    isFirstStep: stepManager.isFirstStep,

    // State
    isLoading: isCreating || isUpdating,
    isSubmitting: isCreating || isUpdating,
  }
}
